import { t } from 'i18next';
import { useCallback, useMemo, useState } from 'react';

import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue, useService } from '@/src/infrastructure/Protocol/useEmblue';
import { DEFAULT_EMAIL_ADDRESS } from '@/src/modules/ContactsModule/constants';
import { isValidCorporativeEmail } from '@/src/modules/ContactsModule/util/VerifyCorporateEmail';
import { ISenderData } from '@/src/presentation/types/interfaces/ISenderData.interface';

export const initialSender: ISenderData = {
  id: undefined,
  cardAddress: '',
  cardCity: '',
  cardIndustry: '',
  cardCountry: '',
  emailFrom: {
    email: '',
  },
  emailReply: {
    email: '',
  },
  nameFrom: '',
  cardState: '',
  cardZip: '',
};
/** cspell:disable */
export enum SenderResponseEnum {
  senderCreate = 'SENDER_GUARDADO_OK',
  senderUpdate = 'SENDER_ACTUALIZADO_OK',
  senderVerify = 'EMAIL_VERIFICACION_ENVIADO_OK',
  emailReplyExisting = 'EMAIL_REMITENTE_EXISTENTE',
  domainKO = 'DOMINIO_REMITENTE_KO',
  emailKO = 'EMAIL_REMITENTE_KO',
  senderEmailVerifyAndAdded = 'EMAIL_VERIFICADO_AGREGADO_OK',
}
/** cspell:enable */
export type CurrentRadioButton = 'email' | 'company';

interface UseSenderModalProps {
  initialNewCreateSender?: boolean;
}

export const useSenderModal = ({ initialNewCreateSender }: UseSenderModalProps) => {
  const service = useService();

  const [createNewSender, setCreateNewSender] = useState(initialNewCreateSender);
  const [saveIsLoading, setSaveIsLoading] = useState(false);

  /** cspell:disable */
  const [senderList, , isLoadingSenderList] = useEmblue(ServiceMethods.getSender, {
    search: '',
    quantity: 0,
    offset: 0,
    orderColumn: 'nombreFrom',
    senseOrder: 'asc',
  });
  /** cspell:enable */

  const senderSelected = useMemo(() => {
    const emailCompany = senderList?.find(
      (sender) => sender.emailFrom?.email !== DEFAULT_EMAIL_ADDRESS
    );

    if (initialNewCreateSender) {
      return { isEmailCompany: false, sender: initialSender };
    }

    if (emailCompany) {
      const isEmailCompany = isValidCorporativeEmail(emailCompany.emailFrom.email);
      return { isEmailCompany, sender: emailCompany };
    } else {
      return { isEmailCompany: false, sender: initialSender };
    }
  }, [initialNewCreateSender, senderList]);

  const updateSender = useCallback(
    async (formValue: ISenderData) => {
      const { data } = await service.updateSender({ sender: formValue });
      /**cspell:disable */
      if (data.id_notificacion === SenderResponseEnum.senderUpdate) {
        return true;
      }
      /**cspell:enable */
      return false;
    },
    [service]
  );

  const validateEmail = useCallback(
    async (email: string) => {
      const isEmailResponse = await service.addVerifiedSender({
        email,
      });

      if (
        isEmailResponse.id_notification === SenderResponseEnum.domainKO ||
        isEmailResponse.id_notification === SenderResponseEnum.emailKO
      ) {
        return { emailId: isEmailResponse.content, isValid: false };
      }
      return { emailId: isEmailResponse.content, isValid: true };
    },
    [service]
  );

  const createSender = useCallback(
    async (formValue: ISenderData) => {
      const { nameFrom, emailFrom, emailReply } = formValue;

      const emailFromResponse = await validateEmail(emailFrom.email);
      let emailReplyResponse = emailFromResponse;
      if (emailFrom !== emailReply) {
        emailReplyResponse = await validateEmail(emailReply.email);
      }

      if (!emailFromResponse.isValid || !emailReplyResponse.isValid) {
        return { ok: false, message: t('CREATE_SENDER_MODAL.notification.emailNoAllowed') };
      }

      const data = await service.createSender({ senderName: nameFrom });
      if (data && data.notification?.id_notification === SenderResponseEnum.senderCreate) {
        const { sender } = data;
        formValue.id = sender.id;
        formValue.default = false;

        formValue.emailFrom.default = true;
        formValue.emailFrom.id = emailFromResponse.emailId.toString();

        formValue.emailReply.default = true;
        formValue.emailReply.id = emailReplyResponse.emailId.toString();

        const senderUpdated = await updateSender(formValue);
        return senderUpdated
          ? { ok: true, message: t('CREATE_SENDER_MODAL.notification.senderCreated') }
          : { ok: false, message: t('CREATE_SENDER_MODAL.notification.errorCreatingSender') };
      }
      return { ok: false, message: t('CREATE_SENDER_MODAL.notification.errorCreatingSender') };
    },
    [service, updateSender, validateEmail]
  );

  const submitData = useCallback(
    async (formValue: ISenderData) => {
      setSaveIsLoading(true);
      if (senderSelected.isEmailCompany) {
        const senderUpdated = await updateSender(formValue);
        setCreateNewSender(false);
        setSaveIsLoading(false);
        return senderUpdated
          ? { ok: true, message: t('CREATE_SENDER_MODAL.notification.senderUpdated') }
          : { ok: false, message: t('CREATE_SENDER_MODAL.notification.senderUpdatedError') };
      } else {
        const senderCreated = await createSender(formValue);
        setCreateNewSender(false);
        setSaveIsLoading(false);
        return senderCreated;
      }
    },
    [createSender, senderSelected.isEmailCompany, updateSender]
  );

  return {
    submitData,
    isLoadingSenderList,
    senderList,
    createNewSender,
    setCreateNewSender,
    isEmailCompany: senderSelected.isEmailCompany,
    senderData: senderSelected.sender,
    saveIsLoading,
  };
};
